// Pages/admin/CreateSubscriptionPage.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiSave,
  FiX,
  FiPlus,
  FiTrash2,
  FiPackage,
  FiDollarSign,
} from "react-icons/fi";
import { useSubscriptionPlanStore } from "../../store/subscriptionPlanStore";
import { useServiceStore } from "../../store/serviceStore";

export default function CreateSubscriptionPage() {
  const navigate = useNavigate();
  const { createSubscription, loading, error, clearError } =
    useSubscriptionPlanStore();
  const { services: availableServices, loadServices } = useServiceStore();

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    isActive: true,
    services: [],
  });

  const [newService, setNewService] = useState({ id: "", quantity: 1 });

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();

    try {
      const subscriptionData = {
        ...formData,
        price: parseFloat(formData.price),
        services: formData.services.map((service) => ({
          id: parseInt(service.id),
          quantity: parseInt(service.quantity),
        })),
      };

      await createSubscription(subscriptionData);
      navigate("/admin/subscriptions");
    } catch (error) {
      console.error("Erreur lors de la création:", error);
    }
  };

  const addService = () => {
    if (newService.id && newService.quantity > 0) {
      const serviceExists = formData.services.find(
        (s) => s.id === newService.id,
      );
      if (!serviceExists) {
        setFormData((prev) => ({
          ...prev,
          services: [...prev.services, { ...newService }],
        }));
        setNewService({ id: "", quantity: 1 });
      }
    }
  };

  const removeService = (serviceId) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.filter((s) => s.id !== serviceId),
    }));
  };

  const getServiceName = (serviceId) => {
    const service = availableServices.find((s) => s.id === parseInt(serviceId));
    return service ? service.nom : `Service #${serviceId}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto">
        {/* En-tête */}
        <div className="mb-8">
          <div className="md:flex space-y-2 items-center justify-between">
            <div>
              <h1 className="font-poppins font-semibold text-[18px] text-title mb-2">
                Créer un nouvel abonnement
              </h1>
              <p className="font-nunito text-[14px] text-text">
                Configurez une nouvelle formule d'abonnement
              </p>
            </div>
            <button
              onClick={() => navigate("/admin/subscriptions")}
              className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 font-nunito font-semibold text-[14px] inline-flex items-center space-x-2 transition-colors"
            >
              <FiX size={20} />
              <span>Annuler</span>
            </button>
          </div>
        </div>

        {/* Formulaire */}
        <div className="bg-white border border-gray-200 shadow-sm">
          <form
            onSubmit={handleSubmit}
            className="p-2 md:p-6 space-y-2 md:space-y-6"
          >
            {/* Message d'erreur */}
            {error && (
              <div className="bg-red-50 border border-red-200 p-4">
                <div className="flex items-center space-x-2 text-red-800">
                  <FiX size={20} />
                  <span className="font-poppins font-medium text-[15px] text-subtitle">
                    {error}
                  </span>
                </div>
              </div>
            )}

            {/* Informations de base */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="font-poppins font-medium text-[15px] text-subtitle block mb-2">
                  Nom de l'abonnement *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 font-nunito text-[14px] text-text focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500"
                  placeholder="Ex: Formule Relaxe"
                />
              </div>

              <div>
                <label className="font-poppins font-medium text-[15px] text-subtitle block mb-2">
                  Prix (FCFA) *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiDollarSign className="text-gray-400" size={20} />
                  </div>
                  <input
                    type="number"
                    required
                    min="0"
                    // step="0.01"
                    value={formData.price}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        price: e.target.value,
                      }))
                    }
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 font-nunito text-[14px] text-text focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500"
                    placeholder="30000"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="font-poppins font-medium text-[15px] text-subtitle block mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 font-nunito text-[14px] text-text focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500"
                placeholder="Ex: 2 massages + 1 gommage"
              />
            </div>

            {/* Services inclus */}
            <div>
              <label className="font-poppins font-medium text-[15px] text-subtitle block mb-4">
                Services inclus
              </label>

              {/* Ajout d'un nouveau service */}
              <div className="md:flex gap-3 mb-4">
                <div className="flex-1">
                  <select
                    value={newService.id}
                    onChange={(e) =>
                      setNewService((prev) => ({ ...prev, id: e.target.value }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 font-nunito text-[14px] text-text focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500"
                  >
                    <option value="">Sélectionner un service</option>
                    {availableServices.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.nom}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="w-32">
                  <input
                    type="number"
                    min="1"
                    value={newService.quantity}
                    onChange={(e) =>
                      setNewService((prev) => ({
                        ...prev,
                        quantity: parseInt(e.target.value) || 1,
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 font-nunito text-[14px] text-text focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500"
                    placeholder="Quantité"
                  />
                </div>
                <button
                  type="button"
                  onClick={addService}
                  disabled={!newService.id}
                  className="bg-secondary-500 hover:bg-secondary-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-4 py-2 font-nunito font-semibold text-[14px] inline-flex items-center space-x-2 transition-colors"
                >
                  <FiPlus size={16} />
                  <span>Ajouter</span>
                </button>
              </div>

              {/* Liste des services ajoutés */}
              {formData.services.length > 0 ? (
                <div className="space-y-3">
                  {formData.services.map((service, index) => (
                    <div
                      key={service.id}
                      className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200"
                    >
                      <div className="flex items-center space-x-3">
                        <FiPackage className="text-gray-400" size={16} />
                        <span className="font-poppins font-medium text-[15px] text-subtitle">
                          {getServiceName(service.id)}
                        </span>
                        <span className="font-nunito text-[14px] text-text">
                          x{service.quantity}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeService(service.id)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 border-2 border-dashed border-gray-300">
                  <FiPackage className="mx-auto text-gray-400 mb-2" size={24} />
                  <p className="font-nunito text-[14px] text-text">
                    Aucun service ajouté
                  </p>
                </div>
              )}
            </div>

            {/* Statut */}
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    isActive: e.target.checked,
                  }))
                }
                className="w-4 h-4 text-secondary-500 border-gray-300 focus:ring-secondary-500"
              />
              <label
                htmlFor="isActive"
                className="font-poppins font-medium text-[15px] text-subtitle"
              >
                Abonnement actif
              </label>
            </div>

            {/* Boutons d'action */}
            <div className="md:flex  justify-end md:space-x-3 pt-2 md:pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate("/admin/subscriptions")}
                className=" bg-gray-100 hover:bg-gray-200 text-gray-800  p-3 font-nunito font-semibold text-[14px] transition-colors"
              >
                <span>Annuler</span>
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-secondary-600 hover:from-secondary-700 text-white p-3  font-nunito font-semibold text-[14px] inline-flex items-center space-x-1 transition-all duration-200 hover:shadow-xl"
              >
                <FiSave size={20} />
                <span>{loading ? "Création..." : "Créer l'abonnement"}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
